/**
 * Modal — a focus-friendly dialog used for confirmations (suspend, reject KYC) and
 * small forms. Closes on Escape and on overlay click; the body is escaped by React.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { Button } from './ui.js';

export function Modal({
  open,
  title,
  description,
  children,
  onClose,
  footer,
  size,
}: {
  open: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  /**
   * `wide` for a panel that is a form rather than a question — the provider editor carries
   * seven fields, each with a sentence of guidance, and at the confirm-dialog width every
   * hint wraps to four lines and the operator scrolls past the field they came for.
   */
  size?: 'wide';
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal${size === 'wide' ? ' modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__head">
          <h2 className="modal__title">{title}</h2>
          {description !== undefined && <p className="modal__desc">{description}</p>}
        </div>
        {children !== undefined && <div className="modal__body">{children}</div>}
        {footer !== undefined && <div className="modal__foot">{footer}</div>}
      </div>
    </div>
  );
}

/** A ready-made confirm dialog for destructive/irreversible actions. */
export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  loading = false,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}): JSX.Element | null {
  return (
    <Modal
      open={open}
      title={title}
      {...(description !== undefined ? { description } : {})}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
